import React, { useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { allUsers, chantiers } from '../utils/mockData';
import { Search, Mail, Phone, MapPin, Briefcase, UserPlus, Camera, Shield, UserCheck, Eye, FileText, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';
import { Role } from '../utils/types';
export function Team() {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | Role>('all');
  const filteredMembers = allUsers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;
    return matchesSearch && matchesRole;
  });
  const getMemberStats = (memberId: string, role: string) => {
    if (role === 'admin' || role === 'direction') {
      const totalPhotos = chantiers.reduce((sum, c) => sum + c.tasks.flatMap(t => t.images).length, 0);
      return {
        activeChantiers: chantiers.filter(c => c.status === 'en_cours').length,
        completedChantiers: chantiers.filter(c => c.status === 'termine').length,
        totalPhotos,
        currentChantier: 'Accès complet',
        isAdmin: true
      };
    }
    if (role === 'bureau') {
      const totalPhotos = chantiers.reduce((sum, c) => sum + c.tasks.flatMap(t => t.images).length, 0);
      return {
        activeChantiers: chantiers.filter(c => c.status === 'en_cours').length,
        completedChantiers: chantiers.filter(c => c.status === 'termine').length,
        totalPhotos,
        currentChantier: 'Gestion administrative',
        isBureau: true
      };
    }
    if (role === 'chef_equipe') {
      const managed = chantiers.filter(c => c.chefEquipeId === memberId);
      const active = managed.filter(c => c.status === 'en_cours');
      const completed = managed.filter(c => c.status === 'termine');
      const totalPhotos = managed.reduce((sum, c) => sum + c.tasks.flatMap(t => t.images).length, 0);
      return {
        activeChantiers: active.length,
        completedChantiers: completed.length,
        totalPhotos,
        currentChantier: active[0]?.name || null,
        isChef: true,
        managedCount: managed.length
      };
    }
    // Technicien
    const assigned = chantiers.filter(c => c.team.some(t => t.id === memberId));
    const active = assigned.filter(c => c.status === 'en_cours');
    const completed = assigned.filter(c => c.status === 'termine');
    const totalPhotos = assigned.reduce((sum, c) => sum + c.tasks.flatMap(t => t.images).length, 0);
    return {
      activeChantiers: active.length,
      completedChantiers: completed.length,
      totalPhotos,
      currentChantier: active[0]?.name || null
    };
  };
  const getRoleBadge = (role: string) => {
    const map: Record<string, {
      label: string;
      color: string;
    }> = {
      admin: {
        label: 'Administrateur',
        color: 'bg-purple-100 text-purple-800 border-purple-200'
      },
      direction: {
        label: 'Direction',
        color: 'bg-blue-100 text-blue-800 border-blue-200'
      },
      bureau: {
        label: 'Bureau',
        color: 'bg-teal-100 text-teal-800 border-teal-200'
      },
      chef_equipe: {
        label: "Chef d'Équipe",
        color: 'bg-amber-100 text-amber-800 border-amber-200'
      },
      technicien: {
        label: 'Technicien',
        color: 'bg-green-100 text-green-800 border-green-200'
      }
    };
    return map[role] || {
      label: role,
      color: 'bg-gray-100 text-gray-800 border-gray-200'
    };
  };
  const getRoleIcon = (role: string) => {
    if (role === 'admin') return <Shield size={14} className="text-white" data-id="element-901" />;
    if (role === 'direction') return <Eye size={14} className="text-white" data-id="element-902" />;
    if (role === 'bureau') return <FileText size={14} className="text-white" data-id="element-903" />;
    if (role === 'chef_equipe') return <UserCheck size={14} className="text-white" data-id="element-904" />;
    return <Wrench size={14} className="text-white" data-id="element-905" />;
  };
  const getRoleIconBg = (role: string) => {
    if (role === 'admin') return 'bg-purple-500';
    if (role === 'direction') return 'bg-blue-500';
    if (role === 'bureau') return 'bg-teal-500';
    if (role === 'chef_equipe') return 'bg-amber-500';
    return 'bg-green-500';
  };
  return <div className="min-h-screen bg-gray-50 pb-20 md:pb-6" data-id="element-906">
      <div className="page-container section-spacing" data-id="element-907">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-908">
          <div data-id="element-909">
            <h1 className="heading-xl" data-id="element-910">Gestion d'Équipe</h1>
            <p className="text-caption mt-1" data-id="element-911">
              {allUsers.length} membres ·{' '}
              {allUsers.filter(m => m.role === 'chef_equipe').length} chefs
              d'équipe ·{' '}
              {allUsers.filter(m => m.role === 'technicien').length}{' '}
              techniciens
            </p>
          </div>
          <Button leftIcon={<UserPlus size={18} data-id="element-913" />} data-id="element-912">Ajouter un membre</Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4" data-id="element-914">
          <div className="relative flex-1" data-id="element-915">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} data-id="element-916" />
            <input type="text" placeholder="Rechercher un membre..." className="input-base pl-10 w-full" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-917" />
          </div>
          <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-gray-300 overflow-x-auto scrollbar-hide" data-id="element-918">
            {([['all', 'Tous'], ['admin', 'Admin'], ['direction', 'Direction'], ['bureau', 'Bureau'], ['chef_equipe', 'Chefs'], ['technicien', 'Techniciens']] as const).map(([role, label]) => <button key={role} onClick={() => setRoleFilter(role as 'all' | Role)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${roleFilter === role ? 'bg-[#1E2A3A] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-919">
                {label}
              </button>)}
          </div>
        </div>

        {filteredMembers.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-920">
            {filteredMembers.map((member, index) => {
          const stats = getMemberStats(member.id, member.role);
          const badge = getRoleBadge(member.role);
          return <motion.div key={member.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: index * 0.05
          }} data-id="element-921">
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow" data-id="element-922">
                    <div className="p-6 space-y-4" data-id="element-923">
                      <div className="flex items-start gap-4" data-id="element-924">
                        <div className="relative" data-id="element-925">
                          <img src={member.avatar} alt={member.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-gray-100" data-id="element-926" />
                          <div className={`absolute -bottom-1 -right-1 w-6 h-6 ${getRoleIconBg(member.role)} rounded-full flex items-center justify-center ring-2 ring-white`} data-id="element-927">
                            {getRoleIcon(member.role)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-928">
                          <h3 className="text-lg font-bold text-gray-900 truncate" data-id="element-929">
                            {member.name}
                          </h3>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${badge.color}`} data-id="element-930">
                            {badge.label}
                          </span>
                        </div>
                      </div>

                      {stats.currentChantier && <div className={`rounded-lg p-3 border ${stats.isAdmin ? 'bg-purple-50 border-purple-100' : stats.isBureau ? 'bg-teal-50 border-teal-100' : stats.isChef ? 'bg-amber-50 border-amber-100' : 'bg-green-50 border-green-100'}`} data-id="element-931">
                          <div className="flex items-center gap-2 text-sm" data-id="element-932">
                            {stats.isAdmin ? <>
                                <Shield size={14} className="text-purple-600 flex-shrink-0" data-id="element-933" />
                                <span className="text-purple-900 font-medium" data-id="element-934">
                                  Accès à tous les chantiers
                                </span>
                              </> : stats.isBureau ? <>
                                <FileText size={14} className="text-teal-600 flex-shrink-0" data-id="element-935" />
                                <span className="text-teal-900 font-medium" data-id="element-936">
                                  Gestion administrative
                                </span>
                              </> : stats.isChef ? <>
                                <UserCheck size={14} className="text-amber-600 flex-shrink-0" data-id="element-937" />
                                <span className="text-amber-900 font-medium truncate" data-id="element-938">
                                  {stats.managedCount} chantier
                                  {(stats.managedCount || 0) > 1 ? 's' : ''}{' '}
                                  géré
                                  {(stats.managedCount || 0) > 1 ? 's' : ''}
                                </span>
                              </> : <>
                                <MapPin size={14} className="text-green-600 flex-shrink-0" data-id="element-939" />
                                <span className="text-green-900 font-medium truncate" data-id="element-940">
                                  {stats.currentChantier}
                                </span>
                              </>}
                          </div>
                        </div>}

                      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-100" data-id="element-941">
                        <div className="text-center" data-id="element-942">
                          <div className="text-xl font-bold text-gray-900" data-id="element-943">
                            {stats.activeChantiers}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-944">Actifs</div>
                        </div>
                        <div className="text-center" data-id="element-945">
                          <div className="text-xl font-bold text-gray-900" data-id="element-946">
                            {stats.completedChantiers}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-947">Terminés</div>
                        </div>
                        <div className="text-center" data-id="element-948">
                          <div className="text-xl font-bold text-gray-900" data-id="element-949">
                            {stats.totalPhotos}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-950">Photos</div>
                        </div>
                      </div>

                      <div className="space-y-2 pt-3 border-t border-gray-100" data-id="element-951">
                        <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-952">
                          <Mail size={14} className="text-gray-400" data-id="element-953" />
                          <span className="truncate" data-id="element-954">{member.email}</span>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2" data-id="element-955">
                        <Button variant="outline" size="sm" className="flex-1" data-id="element-956">
                          Voir profil
                        </Button>
                        <Button variant="ghost" size="sm" className="flex-1" data-id="element-957">
                          Contacter
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>;
        })}
          </div> : <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300" data-id="element-958">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-959">
              <Search className="text-gray-400" size={32} data-id="element-960" />
            </div>
            <h3 className="text-lg font-medium text-gray-900" data-id="element-961">
              Aucun membre trouvé
            </h3>
            <p className="text-gray-500 mt-1" data-id="element-962">
              Essayez de modifier vos filtres.
            </p>
          </div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 border-t border-gray-200" data-id="element-963">
          <Card className="p-6" data-id="element-964">
            <div className="flex items-center gap-4" data-id="element-965">
              <div className="p-3 bg-teal-50 rounded-lg" data-id="element-966">
                <Briefcase className="w-6 h-6 text-[#1DBAB4]" data-id="element-967" />
              </div>
              <div data-id="element-968">
                <p className="text-2xl font-bold text-gray-900" data-id="element-969">
                  {chantiers.filter(c => c.status === 'en_cours').length}
                </p>
                <p className="text-sm text-gray-500" data-id="element-970">Chantiers actifs</p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-971">
            <div className="flex items-center gap-4" data-id="element-972">
              <div className="p-3 bg-green-50 rounded-lg" data-id="element-973">
                <Camera className="w-6 h-6 text-green-600" data-id="element-974" />
              </div>
              <div data-id="element-975">
                <p className="text-2xl font-bold text-gray-900" data-id="element-976">
                  {chantiers.reduce((sum, c) => sum + c.tasks.flatMap(t => t.images).length, 0)}
                </p>
                <p className="text-sm text-gray-500" data-id="element-977">Photos uploadées</p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-978">
            <div className="flex items-center gap-4" data-id="element-979">
              <div className="p-3 bg-amber-50 rounded-lg" data-id="element-980">
                <MapPin className="w-6 h-6 text-amber-600" data-id="element-981" />
              </div>
              <div data-id="element-982">
                <p className="text-2xl font-bold text-gray-900" data-id="element-983">
                  {new Set(chantiers.map(c => c.city)).size}
                </p>
                <p className="text-sm text-gray-500" data-id="element-984">Villes couvertes</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>;
}